import { useForm } from "@tanstack/react-form";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";

import { createTagFn } from "@/actions/tags/handler";
import { sharedContentTopics } from "@/api/db/schema/content";
import { insertTagschema, tagTypeNamesList } from "@/api/db/schema/tag";
import { LoadingButton } from "@/components/loading-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { Dumbbell } from "lucide-react";
import { toast } from "sonner";
import z from "zod";

export const Route = createFileRoute("/_app/forum/create")({
  beforeLoad: async ({ context }) => {
    if (!context.user) {
      throw redirect({ to: "/forum" });
    }
  },
  component: RouteComponent,
});

const data: z.infer<typeof insertTagschema> = {
  tagTitle: "",
  tagContent: "",
  category: "General",
  topics: [],
};

const categoryLabels: Record<(typeof tagTypeNamesList)[number], string> = {
  General: "Genel",
  Powerlifting: "Powerlifting",
  Bodybuilding: "Vücut geliştirme",
  Nutrition: "Beslenme",
  Recovery: "Toparlanma",
  Mental: "Zihinsel hazırlık",
  Equipment: "Ekipman",
  Technique: "Hareket tekniği",
  Community: "Topluluk",
  Events: "Yarışmalar ve etkinlikler",
  Other: "Diğer",
};

function RouteComponent() {
  const tagMutation = useMutation({
    mutationFn: createTagFn,
    onSuccess: () => {
      redirect({ to: "/forum" });
    },
  });
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: data,
    onSubmit: ({ value }) => {
      tagMutation.mutate(
        {
          data: {
            category: z.enum(tagTypeNamesList).parse(value.category),
            tagContent: value.tagContent,
            tagTitle: value.tagTitle,
            topics: value.topics,
          },
        },
        {
          onSuccess: (data) => {
            form.reset();

            toast(
              <div className="flex w-full items-center gap-3">
                <img
                  src="/favicon.ico"
                  alt="Site Logosu"
                  className="h-8 w-8 shrink-0"
                />
                <span className="font-medium text-foreground">
                  <strong className="font-bold text-primary">
                    {data.body.tag.tagTitle}
                  </strong>{" "}
                  başlıklı konu başarıyla oluşturuldu.
                </span>
              </div>,
              {
                action: {
                  label: "Konuya Git",
                  onClick: () => {
                    navigate({
                      to: "/forum/$tagId",
                      params: { tagId: data.body.tag.tagId },
                    });
                  },
                },
                position: "top-center",
              },
            );
          },
          onError: (error) => {
            console.error("Error creating tag:", error);
            toast.error("Konu oluşturulurken bir hata oluştu.", {
              description: error.message,
              icon: <Dumbbell />,
              style: {
                background: "oklch(0.12 0 0)",
                color: "oklch(0.6 0.22 25)",
              },
            });
          },
        },
      );
    },
  });

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background p-4 py-24">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Yeni Konu Oluştur</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-6"
          >
            {/* Konu Bilgileri */}
            <div className="space-y-4">
              <form.Field
                name="tagTitle"
                validators={{
                  onChange: ({ value }) =>
                    !value
                      ? "Başlık boş bırakılamaz."
                      : value.length < 5
                        ? "Başlık en az 5 karakter olmalıdır."
                        : undefined,
                }}
                children={(field) => (
                  <div>
                    <label
                      htmlFor={field.name}
                      className="block pb-2 font-medium text-sm"
                    >
                      Konu Başlığı
                    </label>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Konunuz için bir başlık girin..."
                    />
                    {field.state.meta.isTouched && field.state.meta.errors ? (
                      <p className="mt-2 text-destructive text-sm">
                        {field.state.meta.errors.join(", ")}
                      </p>
                    ) : null}
                  </div>
                )}
              />
              <form.Field
                name="tagContent"
                validators={{
                  onChange: ({ value }) =>
                    !value
                      ? "İçerik boş bırakılamaz."
                      : value.length < 20
                        ? "İçerik en az 20 karakter olmalıdır."
                        : undefined,
                }}
                children={(field) => (
                  <div>
                    <label
                      htmlFor={field.name}
                      className="block pb-2 font-medium text-sm"
                    >
                      İçerik
                    </label>
                    <Textarea
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Konu içeriğini buraya yazın."
                      className="min-h-[150px] resize-y"
                    />
                    {field.state.meta.isTouched && field.state.meta.errors ? (
                      <p className="mt-2 text-destructive text-sm">
                        {field.state.meta.errors.join(", ")}
                      </p>
                    ) : null}
                  </div>
                )}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="font-medium text-lg">Ek Bilgiler</h3>
              <form.Field
                name="category"
                validators={{
                  onChange: ({ value }) =>
                    !value ? "Kategori alanı boş bırakılamaz." : undefined,
                }}
                children={(field) => (
                  <div>
                    <label
                      htmlFor={field.name}
                      className="block pb-2 font-medium text-sm"
                    >
                      Kategori
                    </label>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) =>
                        field.handleChange(value as any)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Kategori seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tagTypeNamesList.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                            {categoryLabels[category] !== category
                              ? ` · ${categoryLabels[category]}`
                              : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.state.meta.isTouched && field.state.meta.errors ? (
                      <p className="mt-2 text-destructive text-sm">
                        {field.state.meta.errors.join(", ")}
                      </p>
                    ) : null}
                  </div>
                )}
              />
              <form.Field
                name="topics"
                children={(field) => (
                  <div>
                    <label className="block pb-2 font-medium text-sm">
                      Konu etiketleri
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {sharedContentTopics.map((topic) => {
                        const currentTopics = field.state.value ?? [];
                        const selected = currentTopics.includes(topic);
                        return (
                          <Button
                            key={topic}
                            type="button"
                            size="sm"
                            variant={selected ? "default" : "outline"}
                            onClick={() =>
                              field.handleChange(
                                selected
                                  ? currentTopics.filter(
                                      (item) => item !== topic,
                                    )
                                  : [...currentTopics, topic],
                              )
                            }
                          >
                            {topic}
                          </Button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-muted-foreground text-xs">
                      Sorunuzu en iyi anlatan bir veya iki etiket seçin.
                    </p>
                  </div>
                )}
              />
            </div>

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <LoadingButton
                  type="submit"
                  disabled={!canSubmit || isSubmitting || tagMutation.isPending}
                  loading={tagMutation.isPending}
                  size="lg"
                  className={cn(
                    "w-full",
                    !canSubmit || isSubmitting || tagMutation.isPending
                      ? "pointer-events-none opacity-50"
                      : "",
                  )}
                >
                  {tagMutation.isPending
                    ? "Konu Oluşturuluyor..."
                    : "Konu Oluştur"}
                </LoadingButton>
              )}
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
